import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import { supabase } from '../lib/supabaseClient'
import '../styles/ProfilePage.css'
import CosmeticsModal from '../components/CosmeticsModal'

/* ─────────────────────────────────────────────
   Route setup (in your router):
     /profile          → własny profil (wymaga auth)
     /profile/:username → publiczny profil gracza
   ───────────────────────────────────────────── */


// ─── AchievementTile ─────────────────────────────────────────────────────────
function AchievementTile({ ach }) {
  const ref = useRef(null)
  const [dir, setDir] = useState('top')

  const calcDir = () => {
    const el = ref.current
    if (!el) return
    const rect     = el.getBoundingClientRect()
    const vW       = window.innerWidth
    const TOOLTIP_W = 200
    const MARGIN    = 12

    const fitsLeft  = rect.left - TOOLTIP_W - 10 >= MARGIN
    const fitsRight = rect.right + TOOLTIP_W + 10 <= vW - MARGIN
    const fitsAbove =
      rect.left - TOOLTIP_W / 2 >= MARGIN &&
      rect.right + TOOLTIP_W / 2 <= vW - MARGIN

    if (fitsAbove)       setDir('top')
    else if (fitsRight)  setDir('right')
    else                 setDir('left')
  }

  const isLocked = Boolean(ach.locked)
  const hasProgress = isLocked && ach.progress_goal != null

  const tooltipClass = dir === 'top'
    ? 'pr-achiv-tooltip'
    : `pr-achiv-tooltip pr-achiv-tooltip--${dir}`

  const progressPct = hasProgress
    ? Math.min(100, Math.round((ach.progress_current / ach.progress_goal) * 100))
    : 0

  return (
    <div
      ref={ref}
      className={`pr-achievement${isLocked ? ' pr-achievement--locked' : ''}`}
      onMouseEnter={calcDir}
      onTouchStart={calcDir}
    >
      {isLocked && <span className="pr-achievement-lock">🔒</span>}
      {ach.icon_url
        ? <img src={ach.icon_url} alt={ach.name} className={isLocked ? 'pr-achievement-img--locked' : ''} />
        : <span style={{ fontSize: '1.6rem', opacity: isLocked ? 0.3 : 1 }}>🏆</span>
      }
      <div className={tooltipClass}>
        <span className="pr-achiv-tooltip-name" style={isLocked ? { color: 'var(--text-muted)' } : {}}>
          {isLocked ? '🔒 ' : ''}{ach.name}
        </span>
        {ach.description && (
          <span className="pr-achiv-tooltip-desc">{ach.description}</span>
        )}
        {hasProgress && (
          <div className="pr-achiv-progress">
            <div className="pr-achiv-progress-track">
              <div
                className="pr-achiv-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="pr-achiv-progress-label">
              {ach.progress_current.toLocaleString()} / {ach.progress_goal.toLocaleString()}
            </span>
          </div>
        )}
        {ach.reward_type && (
          <span className="pr-achiv-tooltip-reward">
            Reward: {ach.reward_type === 'avatar' ? 'Avatar' : 'Border'}
          </span>
        )}
        {!isLocked && ach.ownership_pct !== undefined && (
          <span className="pr-achiv-tooltip-pct">
            Owned by {ach.ownership_pct}% players
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function streakMultClass(streak) {
  if (streak >= 60) return { cls: "x25",  label: "×2.5" }
  if (streak >= 30) return { cls: "x2",   label: "×2.0" }
  if (streak >= 14) return { cls: "x175", label: "×1.75" }
  if (streak >= 7)  return { cls: "x15",  label: "×1.5" }
  if (streak >= 3)  return { cls: "x125", label: "×1.25" }
  return { cls: "x1", label: "×1.0" }
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, profile: ownProfile, refreshProfile } = useUser()
  const navigate             = useNavigate()
  const { username }         = useParams()   // undefined na /profile (własny profil)

  // Czy oglądamy cudzy profil?
  const isPublicView = Boolean(username)
  // isOwnProfile: porównujemy user_id z Supabase z user_id w fetchniętym profilu
  const [isOwnProfile, setIsOwnProfile] = useState(!isPublicView)

  const [profile, setProfile]   = useState(null)
  const [loading, setLoading]   = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [toast, setToast]       = useState(null)
  const [showModal, setShowModal]   = useState(false)

  const getAuthHeader = async () => {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) return {}
    return { Authorization: `Bearer ${token}` }
  }

  // Fetch własnego profilu (wymaga auth)
  const fetchOwnProfile = async () => {
    try {
      const headers = await getAuthHeader()
      if (!headers.Authorization) { setNotFound(true); setLoading(false); return }
      const res  = await fetch('/api/profile', { headers })
      if (res.status === 401) { setNotFound(true); return }
      if (!res.ok) { setNotFound(true); return }
      setProfile(await res.json())
    } catch (e) {
      console.error(e)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  // Fetch publicznego profilu po username (bez auth)
  const fetchPublicProfile = async (uname) => {
    try {
      const res = await fetch(`/api/profile/${encodeURIComponent(uname)}`)
      if (!res.ok) { setNotFound(true); return }
      const data = await res.json()
      setProfile(data)
      // Sprawdź czy to własny profil przez porównanie UUID
      if (user && data.user_id === user.id) {
        setIsOwnProfile(true)
      }
    } catch (e) {
      console.error(e)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isPublicView) {
      fetchPublicProfile(username)
    } else {
      fetchOwnProfile()
    }
  }, [username])


  const copyProfileLink = () => {
    const uname = profile?.username
    if (!uname) return
    const url = `${window.location.origin}/profile/${encodeURIComponent(uname)}`
    navigator.clipboard.writeText(url)
    showToast('Link skopiowany!')
  }

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2200)
  }

  // ── Render states ──
  if (loading) return (
    <div className="pr-root pr-root--loading">
      <div className="pr-loading-spinner" />
    </div>
  )

  if (notFound) return (
    <div className="pr-root pr-root--loaded">
        <div className="pr-container">
          <div className="pr-topbar">
            <button className="pr-back-btn" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="pr-page-title">Profile</h1>
            <div style={{ width: 70 }} />
          </div>
          <div className="pr-not-found">
            Gracz <span style={{ color: 'var(--gold-mid)' }}>"{username}"</span> doesn't exist.
          </div>
      </div>
    </div>
  )

  const lvl    = profile?.level ?? {}
  const streak = profile?.daily_streak ?? 0
  const ps     = profile?.profile_stats ?? {}
  const { cls: multCls, label: multLabel } = streakMultClass(streak)
  const xpPct  = lvl.is_max_level ? 100 : (lvl.progress_pct ?? 0)


  return (
    <div className="pr-root pr-root--loaded">
        <div className="pr-container">

          {/* TOPBAR */}
          <div className="pr-topbar">
            <button className="pr-back-btn" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="pr-page-title">Profile</h1>
            <button className="pr-share-btn" onClick={copyProfileLink}>
              🔗 Share
              <span className="pr-share-tooltip">
                <span className="pr-edit-tooltip-text">Copy link to profile</span>
                <span className="pr-edit-tooltip-arrow" />
              </span>
            </button>
          </div>

          {/* MAIN PANEL */}
          <div className="pr-panel">

            {/* HERO */}
            <div className="pr-section">
            <div className="pr-hero">

              {/* AVATAR */}
              <div className="pr-avatar-wrap">
                {profile?.avatar_url
                  ? <img src={profile.avatar_url} alt="avatar" className="pr-avatar" />
                  : <div className="pr-avatar">🧙</div>
                }
                {profile?.border_url && (
                  <img src={profile.border_url} alt="border" className="pr-border-overlay" />
                )}
                <button
                  className="pr-avatar-edit-btn"
                  style={{ display: isPublicView ? 'none' : 'flex' }}
                  onClick={() => setShowModal(true)}
                >
                  ✎
                  <span className="pr-edit-tooltip">
                    <span className="pr-edit-tooltip-text">Change avatar / border</span>
                    <span className="pr-edit-tooltip-arrow" />
                  </span>
                </button>
              </div>

              <div className="pr-hero-info">
                {/* Frakcja — prawy górny róg */}
                {profile?.fraction && (
                  <img
                    src={`/${profile.fraction.toLowerCase()}.png`}
                    alt={profile.fraction}
                    className="pr-fraction-logo"
                  />
                )}

                <h2 className="pr-username">{profile?.username}</h2>
                <span className="pr-level-badge">Level {lvl.level ?? 1}</span>

                {/* XP Bar */}
                <div className="pr-xp-section">
                  <div className="pr-xp-meta">
                    <span className="pr-xp-label">
                      {lvl.is_max_level ? 'MAX LEVEL' : `${(lvl.current_xp ?? 0).toLocaleString()} / ${(lvl.level_xp ?? 0).toLocaleString()} XP`}
                    </span>
                  </div>
                  <div className="pr-xp-track">
                    <div className="pr-xp-fill" style={{ width: `${xpPct}%` }} />
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div className="pr-divider" />

            {/* STATYSTYKI */}
            <div className="pr-section">
            <p className="pr-card-label">Statistics</p>
            <div className="pr-stats-grid">

              <div className="pr-stat pr-stat--tooltip">
                <div className="pr-stat-key">Daily Streak</div>
                <div className="pr-stat-val pr-stat-val--streak">{ps?.streak ?? 0}</div>
                {ps?.best_streak > 0 && ps.best_streak !== ps?.streak && (
                  <div className="pr-stat-best">BEST&nbsp;{ps.best_streak}</div>
                )}
                <div className="pr-stat-tooltip">
                  <span className="pr-achiv-tooltip-name">Daily Streak</span>
                  <span className="pr-achiv-tooltip-desc">
                    Consecutive days you've played.<br />
                    Longer streaks give XP multipliers!
                  </span>
                  <span className="pr-edit-tooltip-arrow" />
                </div>
              </div>

              <div className="pr-stat">
                <div className="pr-stat-key">Played games</div>
                <div className="pr-stat-val">{ps?.played ?? 0}</div>
              </div>

              <div className="pr-stat">
                <div className="pr-stat-key">Avg. Attempts</div>
                <div className="pr-stat-val">{ps?.avg_attempts ?? '—'}</div>
              </div>

              <div className="pr-stat">
                <div className="pr-stat-key">Win Rate</div>
                <div className={`pr-stat-val ${
                  (ps?.played ?? 0) === 0 ? '' :
                  (ps?.win_rate ?? 0) >= 70 ? 'pr-stat-val--good' :
                  (ps?.win_rate ?? 0) < 30 ? 'pr-stat-val--bad' : ''
                }`}>{(ps?.played ?? 0) === 0 ? '—' : `${ps?.win_rate ?? 0}%`}</div>
              </div>


              <div className="pr-stat">
                <div className="pr-stat-key">First Try</div>
                <div className={`pr-stat-val ${
                  (ps?.played ?? 0) === 0 ? '' :
                  (ps?.first_try_pct ?? 0) >= 70 ? 'pr-stat-val--good' :
                  (ps?.first_try_pct ?? 0) < 30 ? 'pr-stat-val--bad' : ''
                }`}>{(ps?.played ?? 0) === 0 ? '—' : `${ps?.first_try_pct ?? 0}%`}</div>
              </div>

              <div className="pr-stat pr-stat--tooltip">
                <div className="pr-stat-key">Joined</div>
                <div className="pr-stat-val">{ps?.joined ?? '—'}</div>
                {ps?.joined && (
                  <div className="pr-stat-tooltip">
                    <span className="pr-achiv-tooltip-name">
                      {(() => {
                        const [d, m, y] = (ps.joined).split('.')
                        return new Date(`${y}-${m}-${d}`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                      })()}
                    </span>
                    <span className="pr-edit-tooltip-arrow" />
                  </div>
                )}
              </div>

            </div>
            </div>

            <>
              <div className="pr-divider" />
              <div className="pr-section">
              <p className="pr-card-label">
                Achievements&nbsp;
                <span className="pr-ach-total">· {profile?.achievements?.length ?? 0}</span>
                {(profile?.total_achievements ?? 0) > 0 && (
                  <span className="pr-ach-total">/{profile.total_achievements}</span>
                )}
              </p>
              {(profile?.achievements?.length ?? 0) === 0 && (profile?.locked_achievements?.length ?? 0) === 0 ? (
                <div className="pr-achievements-empty">
                  <p className="pr-achievements-empty-title">No achievements yet</p>
                  <p className="pr-achievements-empty-sub">Play daily challenges to unlock your first achievement and prove your worth to Azeroth.</p>
                </div>
              ) : (
                <div className="pr-achievements-grid">
                  {(profile?.achievements ?? []).map(ach => (
                    <AchievementTile key={ach.key} ach={ach} />
                  ))}
                  {isOwnProfile && (profile?.locked_achievements ?? []).map(ach => (
                    <AchievementTile key={`locked-${ach.key}`} ach={ach} />
                  ))}
                </div>
              )}
              </div>
            </>

          </div>{/* end pr-panel */}

        </div>
      {toast && <div className="pr-save-toast">✔ {toast}</div>}

      {showModal && (
        <CosmeticsModal
          onClose={() => setShowModal(false)}
          getAuthHeader={getAuthHeader}
          onSaved={async () => { await fetchOwnProfile(); await refreshProfile(); }}
          showToast={showToast}
        />
      )}

    </div>
  )
}